/**
 * ImmoWerft RealtimeAi – V2
 * Nutzt ein normales Chat-Completion-Modell (statt der OpenAI Realtime-API/WebRTC).
 * - Tool-Calls laufen client-seitig über die bestehenden REST-Endpunkte.
 * - Mikrofon via Whisper-Upload (/openai/transcribe).
 * - Sprachausgabe via Browser SpeechSynthesis.
 * - Chat-Historie, Session-Logging, Router und die externen Event-Listener bleiben erhalten.
 */
import { createPlaceholderRotator } from '../placeholder-rotation.js';

document.addEventListener('DOMContentLoaded', async function () {
    const { buildMessageMeta, isTechnicalFallback, normalizeSessionUrl } = require('./message-log');
    const syncSocketConsentClass = () => {
        const socketFooter = document.querySelector('footer#socket');
        if (socketFooter) {
            socketFooter.classList.toggle(
                'ki-socket-accepted',
                sessionStorage.getItem('mw_ai_consent') === '1'
            );
        }
    };
    syncSocketConsentClass();

    const cfg = await (
        window.mwRealtimeAiConfigReady ||
        Promise.resolve(window.mwRealtimeAiConfig || {})
    ).catch(function (error) {
        console.error('RealtimeAi-Konfiguration konnte nicht geladen werden:', error);
        return window.mwRealtimeAiConfig || {};
    });

    const overlay = document.querySelector('#mwai-assistant-overlay-wrapper');
    if (!overlay) {
        return;
    }

    // ===== Consent-Gating =====
    // WICHTIG: Solange kein Consent gesetzt ist, wird KEINE Verbindung zu OpenAI aufgebaut
    // und es feuert KEINE REST-Route (auch nicht nonce/session/start).
    const ENFOLD_CONSENT_COOKIE = 'aviaCookieConsent';
    const ENFOLD_OPENAI_DISABLED_COOKIE = 'aviaPrivacyCustomCookieopenaiDisabled';

    const hasCookie = (name) => {
        return document.cookie
            .split(';')
            .map(cookie => cookie.trim())
            .some(cookie => cookie.indexOf(name + '=') === 0);
    };

    const isEnfoldConsentActive = () => {
        return !window.CCM && hasCookie(ENFOLD_CONSENT_COOKIE);
    };

    let hasConsent = () => sessionStorage.getItem('mw_ai_consent') === '1';

    // CCM Cookie Checks.
    window.addEventListener('ccm19WidgetLoaded', function (args) {
        if ( true === check_consent_settings() ) {
            sessionStorage.setItem('mw_ai_consent', '1');
            applyConsentState();
            ensureSessionStarted();
            flushPendingConsentActions();
        }
        if ( false === check_consent_settings() ) {
            sessionStorage.removeItem('mw_ai_consent');
            applyConsentState();
        }
    });

    window.addEventListener('ccm19WidgetClosed', function (args) {
        if ( true === check_consent_settings() ) {
            sessionStorage.setItem('mw_ai_consent', '1');
            applyConsentState();
            ensureSessionStarted();
            flushPendingConsentActions();
        }
        if ( false === check_consent_settings() ) {
            sessionStorage.removeItem('mw_ai_consent');
            applyConsentState();
        }
    });

    // Enfold Consent unterstuetzen.
    // Enfold ist aktiv, wenn aviaCookieConsent existiert und kein CCM geladen ist.
    // Der OpenAI-Consent gilt nur, wenn Enfold nicht den Disable-Cookie gesetzt hat.

    /**
     * Zeigt die AI-Chat-Bubble mit dynamischen Texten basierend auf verschiedenen Triggern an.
     *
     * @param {Object} config - Konfigurationsobjekt
     * @param {string} config.visibleText - Der Text, der in der Taskbar sichtbar sein soll.
     * @param {string} config.aiActionText - Der Text/Prompt, der beim Klick verarbeitet wird.
     * @param {number} [config.idleSeconds] - (Optional) Verzögerung in Sekunden, NACHDEM ein Trigger ausgelöst hat.
     *                                        Ohne weiteren Trigger (kein scrollThreshold / customEventName) dient der
     *                                        Wert zusätzlich als Start-Timer.
     * @param {number} [config.afterSeconds] - (Optional) Sekunden ab Initialisierung. Feuert IMMER nach dieser Zeit,
     *                                         unabhängig davon, ob der Nutzer etwas tut, und unabhängig von den
     *                                         anderen Triggern (nutzbar als reiner Timer oder als Fallback zu
     *                                         scrollThreshold / customEventName – es gewinnt, was zuerst kommt).
     * @param {number} [config.autoHideSeconds] - (Optional) Sekunden nach dem Einblenden, bis die Bubble wieder ausgeblendet wird.
     * @param {number|string} [config.scrollThreshold] - (Optional) Pixel (500), Prozent ("50%") ODER CSS-Selector (".my-class").
     * @param {number} [config.scrollOffset=0] - (Optional) Offset in Pixeln.
     * @param {string} [config.customEventName] - (Optional) Name eines Custom Events.
     * @param {boolean} [config.autoClickWhenChatOpen=false] - (Optional) Klickt die Bubble automatisch, wenn der Chat bereits offen ist.
     * @param {string} [config.assistantTextWhenChatOpen] - (Optional) Assistant-Nachricht, die bei bereits offenem Chat proaktiv geschrieben wird.
     */
    window.mw_ai_bubble_setup_chat_bubble_trigger = function({
                                                                 visibleText,
                                                                 aiActionText,
                                                                 idleSeconds = 0,      // Verzögerung in Sekunden
                                                                 afterSeconds = 0,     // Absoluter Timer ab Initialisierung
                                                                 autoHideSeconds,
                                                                 scrollThreshold,      // Kann Selektor, % oder px sein
                                                                 scrollOffset = 0,
                                                                 customEventName,
                                                                 autoClickWhenChatOpen = false,
                                                                 assistantTextWhenChatOpen
                                                             }) {
        let hasTriggered = false;
        let autoHideTimer = null;
        let delayTimer = null;

        function isChatOpen() {
            const currentChatbox = document.getElementById('mwai-assistant-chatbox-wrapper');
            return currentChatbox && currentChatbox.classList.contains('mwai-assistant-chatbox-active');
        }

        function appendSanitizedBubbleNodes(sourceNode, targetNode) {
            const allowedInlineTags = ['strong', 'b', 'em', 'i', 'span'];

            Array.from(sourceNode.childNodes).forEach((node) => {
                if (node.nodeType === Node.TEXT_NODE) {
                    targetNode.appendChild(document.createTextNode(node.textContent || ''));
                    return;
                }

                if (node.nodeType !== Node.ELEMENT_NODE) {
                    return;
                }

                const tagName = node.tagName.toLowerCase();

                if (tagName === 'br') {
                    targetNode.appendChild(document.createElement('br'));
                    return;
                }

                if (tagName === 'p' || tagName === 'div') {
                    if (targetNode.childNodes.length > 0) {
                        targetNode.appendChild(document.createElement('br'));
                        targetNode.appendChild(document.createElement('br'));
                    }
                    appendSanitizedBubbleNodes(node, targetNode);
                    return;
                }

                if (allowedInlineTags.includes(tagName)) {
                    const safeInlineNode = document.createElement(tagName);
                    appendSanitizedBubbleNodes(node, safeInlineNode);
                    targetNode.appendChild(safeInlineNode);
                    return;
                }

                appendSanitizedBubbleNodes(node, targetNode);
            });
        }

        function renderBubbleVisibleText(targetNode, text) {
            const value = text === undefined || text === null ? '' : String(text);
            targetNode.replaceChildren();

            if (/<\/?[a-z][\s\S]*>/i.test(value)) {
                const template = document.createElement('template');
                template.innerHTML = value;
                appendSanitizedBubbleNodes(template.content, targetNode);
                return;
            }

            targetNode.textContent = value;
        }

        // Blendet die Bubble tatsächlich ein.
        function showBubble() {
            const chatBubbleTaskBar = document.querySelector('.mwai-assistant-taskbar-magic-button');
            const currentTextHolder = document.querySelector('.mwai-assistant-taskbar-magic-button-text');

            if (!chatBubbleTaskBar || !currentTextHolder) {
                console.warn('Chat-Bubble-Elemente wurden nicht im DOM gefunden.');
                return;
            }

            currentTextHolder.removeAttribute('data-bubble-btn-clicked');
            const chatOpenAssistantText = assistantTextWhenChatOpen ? String(assistantTextWhenChatOpen).trim() : '';

            if (chatOpenAssistantText && isChatOpen()) {
                chatBubbleTaskBar.style.display = 'none';
                currentTextHolder.setAttribute('data-bubble-btn-clicked', '1');
                addMessageToChat(chatOpenAssistantText, 'assistant', {
                    meta: buildMessageMeta({ source: 'proactive', url: sessionUrl })
                });
                transcript.push({ role: 'assistant', content: chatOpenAssistantText });
                trimTranscript();
                flushBackendLogs();
                return;
            }

            chatBubbleTaskBar.style.display = 'flex';
            renderBubbleVisibleText(currentTextHolder, visibleText);
            currentTextHolder.setAttribute('data-bubble-btn-action', aiActionText);

            if (autoClickWhenChatOpen && isChatOpen()) {
                currentTextHolder.click();
                return;
            }

            // Auto-Hide Timer starten
            if (autoHideSeconds && autoHideSeconds > 0) {
                autoHideTimer = setTimeout(() => {
                    chatBubbleTaskBar.style.display = 'none';
                }, autoHideSeconds * 1000);
            }
        }

        // Kernfunktion: löst genau einmal aus, optional mit Verzögerung in Sekunden.
        function triggerShow(delaySeconds = 0) {
            if (hasTriggered) return;
            hasTriggered = true;

            cleanupTriggers();

            const delay = (delaySeconds && delaySeconds > 0) ? delaySeconds * 1000 : 0;

            if (delay === 0) {
                showBubble();
                return;
            }

            delayTimer = setTimeout(showBubble, delay);
        }

        // Auslösen über einen der Trigger -> wartet zusätzlich idleSeconds
        function triggerShowWithDelay() {
            triggerShow(idleSeconds);
        }

        let idleTimer = null;
        let absoluteTimer = null;
        let scrollHandler = null;
        let customEventHandler = null;
        let observer = null;

        function cleanupTriggers() {
            if (idleTimer) clearTimeout(idleTimer);
            if (absoluteTimer) clearTimeout(absoluteTimer);
            if (scrollHandler) window.removeEventListener('scroll', scrollHandler);
            if (customEventHandler && customEventName) {
                window.removeEventListener(customEventName, customEventHandler);
            }
            if (observer) observer.disconnect();
        }

        function destroy() {
            cleanupTriggers();
            if (delayTimer) clearTimeout(delayTimer);
            if (autoHideTimer) clearTimeout(autoHideTimer);
        }

        // --- TRIGGER 0: Sofort (kein Trigger konfiguriert -> direkt einblenden) ---
        if (!scrollThreshold && !customEventName && !(idleSeconds > 0) && !(afterSeconds > 0)) {
            triggerShowWithDelay();
            return destroy;
        }

        // --- TRIGGER 1: Absoluter Timer ---
        // Feuert IMMER afterSeconds nach der Initialisierung – unabhängig von Nutzeraktivität.
        // Läuft parallel zu allen anderen Triggern; wer zuerst kommt, gewinnt (hasTriggered).
        // Bewusst OHNE zusätzliche idleSeconds-Verzögerung, damit afterSeconds die Gesamtzeit ist.
        if (afterSeconds > 0) {
            absoluteTimer = setTimeout(() => {
                triggerShow(0);
            }, afterSeconds * 1000);
        }

        // --- TRIGGER 2: Rein zeitgesteuert (NUR wenn kein scrollThreshold gesetzt ist) ---
        if (!scrollThreshold && !customEventName && idleSeconds > 0) {
            idleTimer = setTimeout(() => {
                triggerShowWithDelay();
            }, idleSeconds * 1000);
        }

        // --- TRIGGER 3: Scroll-Erkennung / Selektor-Prüfung ---
        if (scrollThreshold !== undefined) {

            // A) CSS-Selektor (z. B. '.page-id-330', '#geowerft-full-wrapper')
            if (typeof scrollThreshold === 'string' && !scrollThreshold.endsWith('%')) {

                const targetElement = document.querySelector(scrollThreshold);

                // SEITEN-CHECK: Wenn das Element auf DIESER Seite NICHT existiert -> Abbruch!
                if (!targetElement) {
                    destroy();      // stoppt auch einen ggf. laufenden afterSeconds-Timer
                    return destroy; // Macht gar nichts auf Seiten ohne das Element
                }

                // Element existiert! Beobachten, wann der Nutzer dort ankommt
                observer = new IntersectionObserver((entries) => {
                    entries.forEach(entry => {
                        if (entry.isIntersecting) {
                            triggerShowWithDelay(); // Löst aus -> wartet 5 Sek -> blendet ein
                        }
                    });
                }, {
                    threshold: 0.2 // 20% des Elements müssen sichtbar sein
                });

                observer.observe(targetElement);

            }
            // B) Prozentualer Wert (z. B. "50%")
            else if (typeof scrollThreshold === 'string' && scrollThreshold.endsWith('%')) {
                scrollHandler = () => {
                    const percentage = parseFloat(scrollThreshold) / 100;
                    const totalHeight = document.documentElement.scrollHeight - window.innerHeight;
                    const thresholdPx = (totalHeight * percentage) + scrollOffset;

                    if (window.scrollY >= thresholdPx) {
                        triggerShowWithDelay();
                    }
                };
                window.addEventListener('scroll', scrollHandler, { passive: true });
            }
            // C) Pixel-Wert
            else {
                scrollHandler = () => {
                    if (window.scrollY >= (Number(scrollThreshold) + scrollOffset)) {
                        triggerShowWithDelay();
                    }
                };
                window.addEventListener('scroll', scrollHandler, { passive: true });
            }
        }

        // --- TRIGGER 4: Custom Event ---
        if (customEventName) {
            customEventHandler = () => triggerShowWithDelay();
            window.addEventListener(customEventName, customEventHandler);
        }

        return destroy;
    };

    // ===== DOM-Referenzen =====
    const chatbox = document.getElementById('mwai-assistant-chatbox-wrapper');
    const toggleTab = document.getElementById('mwai-asisstant-taskbar-chat-toggle-tab');
    const toggleText = document.getElementById('mwai-asisstant-taskbar-chat-toggle-text');
    const chatWindow = chatbox ? chatbox.querySelector('.mwai-assistant-chatbox-inner-content') : null;
    const inputField = document.getElementById('mwai-assistant-taskbar-content-input-text-field');
    const sendBtn = document.querySelector('.mw-aissistant-taskbar-content-input-buttons-speech-send');
    const micBtn = document.querySelector('.mw-aissistant-taskbar-content-input-buttons-speech');
    const ttsBtn = document.querySelector('.mwai-assistant-chatbox-controle-button-mute');
    const clearBtn = document.querySelector('.mwai-assistant-chatbox-controle-button-cleaner');
    const loadingEl = document.querySelector('.mw-ai-v2-loading-indicator');
    const ctaButtons = document.querySelectorAll('.mwai-assistant-chatbox-cta-button.single-faq-example');
    const speechBubble = document.querySelector('.mwai-assistant-taskbar-magic-button');
    const speechBubbleClose = document.querySelector('.mwai-assistant-taskbar-ai-avatar-speech-bubble-close');
    const SPEECH_BUBBLE_DISMISSED_STORAGE_KEY = 'mw_ai_v2_intro_bubble_closed';
    const consentWith = document.getElementById('mwai-assistant-taskbar-content-input-mask-with-consent-wrapper');
    const consentWithout = document.getElementById('mwai-assistant-taskbar-content-input-mask-without-consent-wrapper');
    const consentUnlockBtn = document.querySelector('.mw-ai-v2-consent-unlock');
    const isMobileDevice = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    const quickButtonsWrapper = document.querySelector('#mw-assistant-quick-buttons');
    const infoModalWrapper = document.querySelector( '.mw-assistant-taskbar-content-infomodal-wrapper' );

    if (!chatWindow || !inputField) {
        return;
    }

    document.body.classList.add('mw_ki_chat_active');

    if ( quickButtonsWrapper && quickButtonsWrapper.innerText === '' ) {
        infoModalWrapper.classList.add('no-quick-buttons');
    }

    // Auf Mobilgeräten darf bei geöffnetem Chat nur das Chatfenster scrollen.
    // Das Fixieren von body verhindert zusätzlich das iOS-Safari-Scrollen hinter dem Chat.
    let mobilePageScrollLock = null;

    function isMobileLayout() {
        return isMobileDevice;
    }

    function lockMobilePageScroll() {
        if (!isMobileLayout() || mobilePageScrollLock) return;

        const html = document.documentElement;
        const body = document.body;
        const scrollY = window.scrollY || window.pageYOffset || 0;

        mobilePageScrollLock = {
            scrollY: scrollY,
            htmlOverflow: html.style.overflow,
            htmlOverscrollBehavior: html.style.overscrollBehavior,
            bodyPosition: body.style.position,
            bodyTop: body.style.top,
            bodyLeft: body.style.left,
            bodyWidth: body.style.width,
            bodyOverflow: body.style.overflow,
            touchMoveHandler: function (event) {
                if (!chatbox.contains(event.target)) {
                    event.preventDefault();
                }
            }
        };

        html.style.overflow = 'hidden';
        html.style.overscrollBehavior = 'none';
        body.style.position = 'fixed';
        body.style.top = `-${scrollY}px`;
        body.style.left = '0';
        body.style.width = '100%';
        body.style.overflow = 'hidden';

        document.addEventListener('touchmove', mobilePageScrollLock.touchMoveHandler, {
            passive: false
        });
    }

    function unlockMobilePageScroll() {
        if (!mobilePageScrollLock) return;

        const lock = mobilePageScrollLock;
        const html = document.documentElement;
        const body = document.body;

        document.removeEventListener('touchmove', lock.touchMoveHandler);
        html.style.overflow = lock.htmlOverflow;
        html.style.overscrollBehavior = lock.htmlOverscrollBehavior;
        body.style.position = lock.bodyPosition;
        body.style.top = lock.bodyTop;
        body.style.left = lock.bodyLeft;
        body.style.width = lock.bodyWidth;
        body.style.overflow = lock.bodyOverflow;
        mobilePageScrollLock = null;

        window.scrollTo(0, lock.scrollY);
    }

    // ===== Konfiguration =====
    const REST = cfg.rest || '/wp-json/';
    const MODEL = cfg.model || 'gpt-4o-mini';
    const TOOL_SPECS = cfg.tool_specs || {};
    const DEFAULT_TOOL_NAMES = Array.isArray(cfg.default_tool_names) ? cfg.default_tool_names : Object.keys(TOOL_SPECS);
    const isHistoryEnabled = (cfg.history_enabled !== false);
    const promptId = cfg.running_id || '';
    const TTS_VOICE = cfg.tts_voice || 'alloy';

    const CHAT_HISTORY_KEY = 'mw_ai_chat_history_v2:'
        + window.location.origin + ':'
        + String(cfg.post_id || 0) + ':'
        + String(cfg.running_id || 'default');
    const REDIRECT_CONTEXT_KEY = 'mw_ai_v2_redirect_context';
    const SALUTATION_PREFERENCE_KEY = 'mw_ai_salutation_preference_v1';
    const MAX_CHAT_HISTORY = 30;
    const CHAT_HISTORY_TTL_MS = 24 * 60 * 60 * 1000;
    const MAX_TRANSCRIPT = 60;
    const MAX_TOOL_ITERATIONS = 10;

    // ===== Status-Texte der Warte-Bubble ("Punkte-Bubble") =====
    // Erscheinen klein hinter den drei Punkten, solange die KI arbeitet.
    // Einfach hier anpassen: Key = Funktionsname, Wert = Text.
    // Alternativ als Funktion (args) => Text, um Argumente einzubauen, z.B.:
    //   search_prop: (args) => 'Suche nach Immobilien' + (args.query ? ' zu "' + args.query + '"' : '') + ' ...',
    // Leerer String ('') = für diese Funktion wird kein Status angezeigt.
    const LOADING_STATUS_TEXTS = {
        // --- Immobilien ---
        search_prop:            'Suche nach passenden Immobilien ...',
        search_prop_by_price:   'Suche nach passenden Immobilien im Preisrahmen ...',
        // --- Suchauftrag / Suchwunsch ---
        get_user_search_wish:   'Schaue nach Ihrem Suchauftrag ...',
        create_search_wish:     'Lege Ihren Suchauftrag an ...',
        get_current_user_info:  'Schaue nach Ihren Accountdaten ...',
        // --- Wissen & Inhalte ---
        search_knowledge:       'Durchsuche unsere Unterlagen ...',
        get_vectore_store:      'Lese das passende Dokument ...',
        get_post_by_category:   'Suche passende Beiträge ...',
        read_sitemap_by_name:   'Schaue mir die Seitenstruktur an ...',
        geo_search:             'Suche die Koordinaten des Ortes ...',
        // --- Module ---
        get_module_list:        'Hole die passenden Module ...',
        search_modules:         'Suche nach passenden Modulen ...',
        get_module_details:     'Hole die Details zum Modul ...',
        // --- Aktionen ---
        write_mail:             'Bereite die E-Mail vor ...',
        send_data_to_webhook:   'Übermittle deine Daten ...',
        request_open_link:      'Öffne die passende Seite ...',
        // --- Seiteninteraktion ---
        click_class_by_query:   'Bediene die Seite für dich ...',
        fill_value_by_query:    'Fülle das Formular für dich aus ...',
        check_if_css_selector_exists:     'Prüfe den Status ...',
        read_html_by_query:     'Lese den Seiteninhalt ...',
        read_html_by_query_all: 'Lese den Seiteninhalt ...',
        read_html_value_by_query: 'Lese den Seiteninhalt ...',
        show_dialog: 'Öffne den Dialog ...'
    };
    // Text, solange die KI "nur" nachdenkt (kein Funktionsaufruf aktiv).
    const LOADING_STATUS_THINKING = 'Einen Moment, ich denke nach ...';
    // Fallback für Funktionen, die oben (noch) nicht aufgeführt sind.
    const LOADING_STATUS_FALLBACK = 'Einen Moment, ich hole die Infos ...';
    // Optionales Überschreiben von außen (ohne Neu-Build), z.B. im Theme:
    //   window.mwAiV2LoadingStatusTexts = { search_prop: 'Ich stöbere im Angebot ...' };
    Object.assign(LOADING_STATUS_TEXTS, window.mwAiV2LoadingStatusTexts || {});

    // Bei V2 entscheidet der Backend (Router) immer den Slot basierend auf User-Input

    // ===== State =====
    // Session-ID + Token kommen bereits serverseitig aus der Config (kein session/start-Request nötig).
    let sessionId = cfg.session_id || null;
    let sessionSecurityToken = cfg.security_token || null;
    let isConnectionReady = false;
    let sessionReadyPromise = null;
    let messageQueue = [];
    let transcript = []; // laufender Gesprächsverlauf für die Chat-Completions-API
    let isWaitingForResponse = false;
    let isTurnBusy = false; // true zwischen Absenden und Eintreffen der Textantwort (Router+Antwort)
    let currentTurnId = null;
    let currentTurnToken = '';
    let currentTurnSource = 'user';
    let currentIntent = '';
    let currentIntentLabel = '';
    const sessionUrl = normalizeSessionUrl(window.location.href);
    const inputPlaceholderDefault = (inputField && inputField.getAttribute('placeholder')) || '';
    let placeholderRotationConfig = {};
    try {
        placeholderRotationConfig = JSON.parse(inputField?.getAttribute('data-placeholder-rotation') || '{}');
    } catch (error) {
        console.warn('[Realtime][V2] Ungültige Placeholder-Konfiguration:', error);
    }
    const placeholderRotator = createPlaceholderRotator(inputField, placeholderRotationConfig);
    let chatStarted = false;
    let hasReplayedHistoryForSession = false;
    let pendingRedirectUrl = null;

    let isAudioEnabled = sessionStorage.getItem('mw_ai_audio_enabled') === 'true';
    let ttsAudio = null; // <audio> für die OpenAI-Sprachausgabe
    let ttsRequestSeq = 0; // laufende TTS-Anfrage identifizieren (verhindert veraltete Wiedergabe)
    let isRecording = false;
    let isMicStarting = false;
    let mediaRecorder = null;
    let mediaChunks = [];
    let micStream = null;
    let submitAfterRecordingStop = false;
    let stopRecordingAfterStart = false;
    let micStreamReleaseTimer = null;
    let micStartPreviousPlaceholder = '';
    const MIC_STREAM_IDLE_TIMEOUT_MS = 30000;

    // ===== Historie (localStorage) =====
    const loadChatHistory = () => {
        if (!isHistoryEnabled) return [];
        try {
            const raw = localStorage.getItem(CHAT_HISTORY_KEY);
            if (!raw) return [];
            const parsed = JSON.parse(raw);
            if (Array.isArray(parsed)) {
                localStorage.removeItem(CHAT_HISTORY_KEY);
                return [];
            }
            const savedAt = Number(parsed && parsed.savedAt ? parsed.savedAt : 0);
            if (!savedAt || Date.now() - savedAt > CHAT_HISTORY_TTL_MS) {
                localStorage.removeItem(CHAT_HISTORY_KEY);
                return [];
            }
            return Array.isArray(parsed.history) ? mergeChatHistories(parsed.history) : [];
        } catch (e) {
            try { localStorage.removeItem(CHAT_HISTORY_KEY); } catch (removeError) {}
            return [];
        }
    };
    const saveChatHistory = (history) => {
        if (!isHistoryEnabled) return;
        try {
            localStorage.setItem(CHAT_HISTORY_KEY, JSON.stringify({
                savedAt: Date.now(),
                history: Array.isArray(history) ? history : []
            }));
        } catch (e) {}
    };
    const mergeChatHistories = (...histories) => {
        const merged = [];
        const seen = new Set();
        histories.flat().forEach((message) => {
            if (!message || (message.role !== 'user' && message.role !== 'assistant') || !message.text) return;
            const key = JSON.stringify([
                message.role,
                String(message.text),
                String(message.linkHref || ''),
                String(message.linkLabel || '')
            ]);
            if (seen.has(key)) return;
            seen.add(key);
            merged.push(message);
        });
        return merged;
    };
    const getSalutationPreference = () => {
        try {
            const value = localStorage.getItem(SALUTATION_PREFERENCE_KEY);
            return value === 'du' || value === 'sie' ? value : '';
        } catch (e) {
            return '';
        }
    };
    const setSalutationPreference = (value) => {
        if (value !== 'du' && value !== 'sie') return;
        try {
            localStorage.setItem(SALUTATION_PREFERENCE_KEY, value);
        } catch (e) {}
    };
    const updateSalutationPreferenceFromText = (text) => {
        const normalized = String(text || '')
            .toLowerCase()
            .normalize('NFD')
            .replace(/[\u0300-\u036f]/g, '')
            .replace(/ß/g, 'ss');

        if (/\b(siez(e|en)|gesiezt|sie-form|h(o|oe)flichkeitsform|f(o|oe)rmlich(e|er|es|en)?\s+anrede|per\s+sie|wieder\s+(auf\s+|per\s+|zum\s+|zur\s+)?sie(-form)?|zur(u|ue)ck\s+(auf\s+|zum\s+|zur\s+)?sie(-form)?|lieber\s+(wieder\s+)?sie|bitte\s+(wieder\s+)?siez(e|en)|sprechen\s+sie\s+mich\s+(bitte\s+)?(wieder\s+)?mit\s+sie\s+an|ich\s+m(o|oe)chte\s+(bitte\s+)?(wieder\s+)?gesiezt\s+werden|nicht\s+(mehr\s+)?duz(e|en)|duz(e|en)?\s+mich\s+(bitte\s+)?nicht\s+(mehr\s+)?|h(o|oe)r(en)?\s+(sie\s+)?auf\s+mich\s+zu\s+duz(e|en)|bleib(en)?\s+wir\s+(lieber\s+)?beim\s+sie)\b/.test(normalized)) {
            setSalutationPreference('sie');
            return;
        }

        if (/\b(du\s+darfst|darfst\s+mich|kannst\s+(du\s+)?mich|sag\s+(ruhig\s+)?du|nenn\s+mich\s+du|duz(e|en)|per\s+du|du-form)\b/.test(normalized)) {
            setSalutationPreference('du');
        }
    };
    const rememberRedirectContext = (url) => {
        if (!url) return;
        try {
            const history = loadChatHistory().slice(-MAX_CHAT_HISTORY);
            sessionStorage.setItem(REDIRECT_CONTEXT_KEY, JSON.stringify({
                url: String(url),
                history: history,
                createdAt: Date.now()
            }));
        } catch (e) {}
    };
    const consumeRedirectContext = () => {
        let raw = '';
        try {
            raw = sessionStorage.getItem(REDIRECT_CONTEXT_KEY) || '';
            sessionStorage.removeItem(REDIRECT_CONTEXT_KEY);
        } catch (e) {
            return null;
        }
        if (!raw) return null;
        try {
            const data = JSON.parse(raw);
            const createdAt = Number(data.createdAt || 0);
            if (!createdAt || Date.now() - createdAt > 5 * 60 * 1000) {
                return null;
            }
            const url = data.url ? String(data.url) : '';
            const history = Array.isArray(data.history) ? data.history.filter((message) => {
                return message && (message.role === 'user' || message.role === 'assistant') && message.text;
            }).slice(-MAX_CHAT_HISTORY) : [];
            return {
                history: history,
                systemMessage: 'Systemhinweis: Die Weiterleitung per request_open_link wurde bereits ausgeführt. Ziel-URL: ' + url + '. Die aktuelle Seite ist die Zielseite der Weiterleitung. Nicht erneut weiterleiten, außer der Nutzer fordert ausdrücklich eine neue Navigation an.'
            };
        } catch (e) {
            return null;
        }
    };
    const persistHistoryMessage = (role, text, linkHref = '', linkLabel = '', meta = {}) => {
        if (!text) return;
        if (isHistoryEnabled) {
            const history = loadChatHistory();
            history.push({ role, text, linkHref, linkLabel });
            if (history.length > MAX_CHAT_HISTORY) {
                history.splice(0, history.length - MAX_CHAT_HISTORY);
            }
            saveChatHistory(history);
        }
        // Backend-Logging NICHT sofort feuern, sondern zwischenspeichern und erst nach
        // Router + Antwort/TTS senden (maximale Geschwindigkeit für den kritischen Pfad).
        queueBackendLog(role, text, 'text', meta);
    };

    // ===== Session / Backend =====
    const generateSessionId = () => {
        return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
            const r = Math.random() * 16 | 0;
            const v = c === 'x' ? r : (r & 0x3 | 0x8);
            return v.toString(16);
        });
    };
    const buildHeaders = () => {
        const headers = { 'Content-Type': 'application/json' };
        if (cfg.nonce) {
            headers['X-WP-Nonce'] = cfg.nonce;
        }
        return headers;
    };
    const startSession = async () => {
        if (!sessionId) {
            sessionId = generateSessionId();
        }
        try {
            const response = await fetch(REST + 'immowerft/v2/realtime/session/start', {
                method: 'POST',
                headers: buildHeaders(),
                body: JSON.stringify({ session_id: sessionId, prompt_id: promptId })
            });
            const data = await response.json();
            if (data.security_token) {
                sessionSecurityToken = data.security_token;
            }
        } catch (e) {}
    };
    const logMessageToBackend = async (role, content, type = 'text', meta = {}) => {
        if (!sessionId || !content) return;
        try {
            await fetch(REST + 'immowerft/v2/realtime/session/message', {
                method: 'POST',
                headers: buildHeaders(),
                body: JSON.stringify({ session_id: sessionId, role: role, content: content, type: type, meta: meta })
            });
        } catch (e) {}
    };
    // Zwischenspeicher für Backend-Logs, damit diese erst NACH Router + Antwort gesendet werden.
    let pendingBackendLogs = [];
    const queueBackendLog = (role, content, type = 'text', meta = {}) => {
        if (!content) return;
        pendingBackendLogs.push({ role, content, type, meta });
    };
    const flushBackendLogs = async () => {
        if (!pendingBackendLogs.length) return;
        const logs = pendingBackendLogs.slice();
        pendingBackendLogs = [];
        // Sequenziell senden (nicht parallel): sonst würden zwei gleichzeitige Requests die
        // Lazy-Session-Erstellung doppelt auslösen. Läuft im Hintergrund, blockiert nichts.
        for (const l of logs) {
            const meta = { ...(l.meta || {}) };
            if (!meta.intent && meta.source !== 'proactive' && meta.source !== 'system_error') {
                meta.intent = currentIntent;
                meta.intent_label = currentIntentLabel;
            }
            await logMessageToBackend(l.role, l.content, l.type || 'text', meta);
        }
    };
    // Lesbare Beschreibung eines Funktionsaufrufs für den Verlauf (z.B. "request_open_link → https://…").
    const describeToolCall = (name, argsRaw, output) => {
        let args = {};
        try { args = argsRaw ? (typeof argsRaw === 'string' ? JSON.parse(argsRaw) : argsRaw) : {}; } catch (e) {}
        if (name === 'request_open_link') {
            let link = '';
            try { const o = JSON.parse(output); link = (o && o.message && o.message.link_to_open) || (o && o.link_to_open) || ''; } catch (e) {}
            if (!link) link = args.link_to_open || '';
            return link ? (name + ' → ' + link) : name;
        }
        const keys = Object.keys(args);
        if (keys.length) {
            const summary = keys.map(k => k + ': ' + String(args[k])).join(', ');
            return name + ' (' + summary + ')';
        }
        return name;
    };
    const endSession = () => {
        if (!sessionId) return;
        try {
            fetch(REST + 'immowerft/v2/realtime/session/end', {
                method: 'POST',
                headers: buildHeaders(),
                body: JSON.stringify({ session_id: sessionId }),
                keepalive: true
            }).catch(() => {});
        } catch (e) {}
    };

    const currentInstructions = () => ''; // Backend entscheidet Instructions basierend auf Slot
    const currentToolNames = () => []; // Backend nutzt Slot-spezifische Tools

    const check_consent_settings = () => {
        let user_is_okay_with_ki_use = false;
        if ( window.CCM ) {
            const ki_cookie_name = 'ChatGPT / OpenAI API';
            const acceptedEmbeddings = Array.isArray(window.CCM.acceptedEmbeddings) ? window.CCM.acceptedEmbeddings : [];
            acceptedEmbeddings.forEach(item => {
                if (ki_cookie_name === item.name) {
                    user_is_okay_with_ki_use = true;
                }
            });
        }

        if (
            isEnfoldConsentActive() &&
            !hasCookie(ENFOLD_OPENAI_DISABLED_COOKIE)
        ) {
            user_is_okay_with_ki_use = true;
        }

        return user_is_okay_with_ki_use;
    }

    const syncConsentSessionState = () => {
        if ( true === check_consent_settings() ) {
            sessionStorage.setItem('mw_ai_consent', '1');
            return;
        }

        if ( window.CCM || hasCookie(ENFOLD_CONSENT_COOKIE) ) {
            sessionStorage.removeItem('mw_ai_consent');
        }
    };

    // ===== UI-Helfer =====
    const hideCtaOnFirstMessage = () => {
        if (chatStarted) return;
        chatStarted = true;
        const ctaWrap = document.querySelector('.mwai-assistant-chatbox-cta-buttons-wrapper');
        if (ctaWrap) ctaWrap.style.display = 'none';
    };
    const addMessageToChat = (text, role = 'user', options = {}) => {
        if (!chatWindow || !text) return;
        const { linkHref, linkLabel, persist = true, meta = {} } = options;
        hideCtaOnFirstMessage();

        const msgDiv = document.createElement('div');
        msgDiv.className = 'mw-ai-v2-msg mw-ai-v2-msg--' + role;
        const bubble = document.createElement('div');
        bubble.className = 'mw-ai-v2-msg__bubble';

        if (linkHref) {
            if (text) bubble.appendChild(document.createTextNode(text + ' '));
            const linkEl = document.createElement('a');
            linkEl.href = linkHref;
            linkEl.target = '_blank';
            linkEl.rel = 'noopener noreferrer';
            linkEl.textContent = linkLabel || linkHref;
            bubble.appendChild(linkEl);
        } else {
            bubble.textContent = text;
        }
        msgDiv.appendChild(bubble);
        chatWindow.appendChild(msgDiv);

        if (persist) {
            persistHistoryMessage(role, text, linkHref || '', linkLabel || '', meta);
        }
        setTimeout(() => { chatWindow.scrollTop = chatWindow.scrollHeight; }, 0);
    };
    // Eingabe während laufender Antwort sperren ("Bitte warten"), danach wieder freigeben.
    const setInputBusy = (busy) => {
        if (inputField) {
            inputField.disabled = busy;
            inputField.placeholder = busy ? 'Bitte warten...' : inputPlaceholderDefault;
        }
        if (sendBtn) sendBtn.disabled = busy;
        if (micBtn) micBtn.disabled = busy;
    };
    // ===== Status-Text in der Warte-Bubble =====
    let loadingStatusEl = null;
    const getLoadingStatusEl = () => {
        if (loadingStatusEl && loadingStatusEl.isConnected) return loadingStatusEl;
        if (!loadingEl) return null;
        loadingStatusEl = loadingEl.querySelector('.mw-ai-v2-loading-status');
        if (!loadingStatusEl) {
            // Fallback: Span anlegen, falls das Template noch ohne Status-Element ausgeliefert wird.
            const bubble = loadingEl.querySelector('.mw-ai-v2-msg__bubble') || loadingEl;
            loadingStatusEl = document.createElement('span');
            loadingStatusEl.className = 'mw-ai-v2-loading-status';
            loadingStatusEl.setAttribute('aria-live', 'polite');
            bubble.appendChild(loadingStatusEl);
        }
        return loadingStatusEl;
    };
    const setLoadingStatus = (text) => {
        const el = getLoadingStatusEl();
        if (!el) return;
        const value = text ? String(text) : '';
        el.textContent = value;
        el.style.display = value ? '' : 'none';
        if (chatWindow) chatWindow.scrollTop = chatWindow.scrollHeight;
    };
    // Status passend zum gerade laufenden Funktionsaufruf setzen (siehe LOADING_STATUS_TEXTS).
    const setLoadingStatusForTool = (name, argsRaw) => {
        if (!name) {
            setLoadingStatus(LOADING_STATUS_THINKING);
            return;
        }
        const entry = LOADING_STATUS_TEXTS[name];
        if (typeof entry === 'function') {
            let args = {};
            try { args = argsRaw ? (typeof argsRaw === 'string' ? JSON.parse(argsRaw) : argsRaw) : {}; } catch (e) {}
            let out = '';
            try { out = entry(args, name) || ''; } catch (e) { out = ''; }
            setLoadingStatus(out);
            return;
        }
        setLoadingStatus(entry === undefined ? LOADING_STATUS_FALLBACK : entry);
    };

    const showLoadingIndicator = () => {
        setLoadingStatus(LOADING_STATUS_THINKING);
        if (loadingEl) {
            loadingEl.style.display = 'block';
            chatWindow.scrollTop = chatWindow.scrollHeight;
        }
        isWaitingForResponse = true;
        isTurnBusy = true;
        setInputBusy(true);
    };
    const hideLoadingIndicator = () => {
        if (loadingEl) {
            loadingEl.style.display = 'none';
        }
        setLoadingStatus('');
        isWaitingForResponse = false;
        // Antwort-Text ist da -> Eingabe wieder erlauben (TTS darf im Hintergrund weiterlaufen).
        isTurnBusy = false;
        setInputBusy(false);
    };

    // ===== Sprachausgabe (OpenAI TTS) =====
    // Antwort wird in Sätze zerlegt: der erste (kurze) Satz wird sofort geholt & abgespielt,
    // die nächsten währenddessen bereits vorgeladen -> deutlich früherer Tonbeginn.
    const setSpeakingState = (speaking) => {
        if (ttsBtn) ttsBtn.classList.toggle('is-speaking', speaking && isAudioEnabled);
    };
    const splitIntoTtsChunks = (text) => {
        const sentences = String(text).match(/[^.!?…\n]+[.!?…]*(\s+|$)/g) || [text];
        const chunks = [];
        let cur = '';
        sentences.forEach(s => {
            if (cur && (cur + s).length > 180) {
                chunks.push(cur.trim());
                cur = s;
            } else {
                cur += s;
            }
        });
        if (cur.trim()) chunks.push(cur.trim());
        return chunks;
    };
    const fetchTtsSrc = async (text, seq) => {
        try {
            const res = await fetch(REST + 'immowerft/v2/openai/tts', {
                method: 'POST',
                headers: buildHeaders(),
                body: JSON.stringify({
                    text: text,
                    voice: TTS_VOICE,
                    running_id: promptId,
                    session_id: sessionId,
                    security_token: sessionSecurityToken
                })
            });
            const data = await res.json().catch(() => null);
            if (seq !== ttsRequestSeq || !data || !data.ok || !data.audio) return null;
            return 'data:' + (data.mime || 'audio/mpeg') + ';base64,' + data.audio;
        } catch (e) {
            return null;
        }
    };
    const playTtsSrc = (src, seq) => {
        return new Promise((resolve) => {
            if (seq !== ttsRequestSeq || !isAudioEnabled) { resolve(); return; }
            if (!ttsAudio) ttsAudio = new Audio();
            ttsAudio.onended = () => resolve();
            ttsAudio.onerror = () => resolve();
            ttsAudio.src = src;
            ttsAudio.play().catch(() => resolve());
        });
    };
    const stopSpeaking = () => {
        ttsRequestSeq++; // laufende/ausstehende Wiedergabe entwerten
        if (ttsAudio) {
            try { ttsAudio.pause(); ttsAudio.currentTime = 0; } catch (e) {}
        }
        setSpeakingState(false);
    };
    const speak = async (text, options = {}) => {
        const { onStart, onUnavailable } = options;
        if (!isAudioEnabled || !text) {
            if (typeof onUnavailable === 'function') onUnavailable();
            return;
        }
        const seq = ++ttsRequestSeq;
        const normalizedText = String(text).trim();
        if (!normalizedText) {
            if (typeof onUnavailable === 'function') onUnavailable();
            return;
        }

        setSpeakingState(true);
        // Primär: kompletter Antworttext in einem TTS-Request.
        const fullSrc = await fetchTtsSrc(normalizedText, seq);
        if (seq !== ttsRequestSeq || !isAudioEnabled) {
            setSpeakingState(false);
            if (typeof onUnavailable === 'function') onUnavailable();
            return;
        }
        if (fullSrc) {
            if (typeof onStart === 'function') onStart();
            await playTtsSrc(fullSrc, seq);
            if (seq === ttsRequestSeq) setSpeakingState(false);
            return;
        }

        // Fallback: Falls der komplette Text serverseitig abgelehnt wird, auf Chunks zurückfallen.
        const chunks = splitIntoTtsChunks(normalizedText);
        if (!chunks.length) {
            setSpeakingState(false);
            if (typeof onUnavailable === 'function') onUnavailable();
            return;
        }

        const chunkSrcs = [];
        for (let i = 0; i < chunks.length; i++) {
            if (seq !== ttsRequestSeq || !isAudioEnabled) {
                setSpeakingState(false);
                if (typeof onUnavailable === 'function') onUnavailable();
                return;
            }
            const src = await fetchTtsSrc(chunks[i], seq);
            if (seq !== ttsRequestSeq || !isAudioEnabled) {
                setSpeakingState(false);
                if (typeof onUnavailable === 'function') onUnavailable();
                return;
            }
            if (!src) {
                setSpeakingState(false);
                if (typeof onUnavailable === 'function') onUnavailable();
                return;
            }
            chunkSrcs.push(src);
        }
        if (typeof onStart === 'function') onStart();
        for (let i = 0; i < chunkSrcs.length; i++) {
            if (seq !== ttsRequestSeq || !isAudioEnabled) {
                setSpeakingState(false);
                return;
            }
            await playTtsSrc(chunkSrcs[i], seq);
        }
        if (seq === ttsRequestSeq) setSpeakingState(false);
    };
    const updateTtsButton = () => {
        if (!ttsBtn) return;
        ttsBtn.classList.toggle('is-active', isAudioEnabled);
        ttsBtn.classList.toggle('is-off', !isAudioEnabled);
        ttsBtn.setAttribute('data-state', isAudioEnabled ? 'enabled' : 'disabled');
        const label = ttsBtn.querySelector('.mwai-assistant-chatbox-controle-button-label');
        if (label) label.textContent = isAudioEnabled ? 'Sprachausgabe an' : 'Sprachausgabe aus';
    };
    updateTtsButton();

    // ===== Chat-Completion-Kern =====
    const trimTranscript = () => {
        while (transcript.length > MAX_TRANSCRIPT) {
            transcript.shift();
        }
        // Verwaisten Tool-Eintrag am Anfang vermeiden
        while (transcript.length && transcript[0].role === 'tool') {
            transcript.shift();
        }
    };
    const buildApiMessages = () => {
        const messages = [{ role: 'system', content: currentInstructions() }];
        transcript.forEach(m => messages.push(m));
        return messages;
    };
    // V2 OPTIMIERT: Router + Chat kombiniert in einem Call
    const postChatV2Combined = async () => {
        const historyTail = transcript.slice(Math.max(0, transcript.length - 20));
        const res = await fetch(REST + 'immowerft/v2/openai/chatcompletion-v2', {
            method: 'POST',
            headers: buildHeaders(),
            body: JSON.stringify({
                model: MODEL,
                messages: buildApiMessages(),
                tools: currentToolNames(),
                running_id: promptId,
                session_id: sessionId,
                security_token: sessionSecurityToken,
                turn_id: currentTurnId,
                ...(currentTurnToken ? { turn_token: currentTurnToken } : {}),
                post_id: mwRealtimeAiConfig.post_id || 0,
                history: historyTail,
                salutation_preference: getSalutationPreference()
            })
        });
        const result = await res.json().catch(() => null);
        if (result?.session_id && result.session_id !== sessionId) {
            sessionId = result.session_id;
        }
        if (result?.turn_token) {
            currentTurnToken = result.turn_token;
        }
        return result;
    };

    const SERVER_TOOL_ENDPOINTS = {
        write_mail: 'immowerft/v2/openai/realtime/write_mail',
        search_knowledge: 'immowerft/v2/openai/realtime/search_knowledge',
        get_vectore_store: 'immowerft/v2/openai/realtime/get_vectore_store',
        get_post_by_category: 'immowerft/v2/openai/realtime/get_post_by_category',
        get_module_list: 'immowerft/v2/openai/realtime/get_module_list',
        search_prop: 'immowerft/v2/openai/realtime/search_prop',
        search_modules: 'immowerft/v2/openai/realtime/search_modules',
        get_module_details: 'immowerft/v2/openai/realtime/get_module_by_id',
        send_data_to_webhook: 'immowerft/v2/openai/realtime/function',
        read_sitemap_by_name: 'immowerft/v2/openai/realtime/read_sitemap_by_name',
        geo_search:          'immowerft/v2/openai/realtime/geo_search',
        get_user_search_wish: 'immowerft/v2/openai/realtime/get_user_search_wish',
        get_current_user_info: 'immowerft/v2/openai/realtime/get_current_user_info',
        create_search_wish: 'immowerft/v2/openai/realtime/create_search_wish',
        single_whitelist_meta_prompt: 'immowerft/v2/openai/realtime/single_whitelist_meta_prompt',
        search_prop_by_price: 'immowerft/v2/openai/realtime/search_prop_by_price'
    };

    const appendQueryParam = (url, key, value) => {
        if (!url) return url;
        const sep = url.includes('?') ? '&' : '?';
        return url + sep + encodeURIComponent(key) + '=' + encodeURIComponent(value);
    };

    const callServerTool = async (path, args) => {
        try {
            const payload = {
                ...args,
                running_id: promptId,
                session_id: sessionId,
                security_token: sessionSecurityToken,
                post_id: mwRealtimeAiConfig.post_id || 0
            };

            if (path === SERVER_TOOL_ENDPOINTS.write_mail) {
                payload.chat_history = transcript
                    .filter(item => {
                        return item
                            && (item.role === 'user' || item.role === 'assistant')
                            && typeof item.content === 'string'
                            && item.content.trim() !== '';
                    })
                    .slice(-20)
                    .map(item => ({
                        role: item.role,
                        content: item.content.trim()
                    }));
            }

            const res = await fetch(REST + path, {
                method: 'POST',
                headers: buildHeaders(),
                body: JSON.stringify(payload)
            });
            const data = await res.json().catch(() => ({}));
            return JSON.stringify(data || {});
        } catch (err) {
            return JSON.stringify({ ok: false, error: String(err) });
        }
    };

    const executeToolCall = async (name, argsRaw) => {
        let args = {};
        try {
            args = argsRaw ? (typeof argsRaw === 'string' ? JSON.parse(argsRaw) : argsRaw) : {};
        } catch (e) {
            args = {};
        }

        // Frontend-Tools (kein Server-Endpunkt)
        if (name === 'click_class_by_query') {
            if (args.selector && document.querySelector(args.selector)) {
                document.querySelector(args.selector).click();
            }
            return JSON.stringify({ ok: true });
        }
        if (name === 'fill_value_by_query') {
            const field = args.selector ? document.querySelector(args.selector) : null;

            if (field) {
                const value = args.value === undefined || args.value === null
                    ? ''
                    : String(args.value);

                // Den nativen Setter verwenden, damit auch kontrollierte Felder
                // (z. B. React) die Änderung erkennen.
                const valueSetter = Object.getOwnPropertyDescriptor(
                    Object.getPrototypeOf(field),
                    'value'
                )?.set;

                if (valueSetter) {
                    valueSetter.call(field, value);
                } else {
                    field.value = value;
                }

                // Viele mobile Formulare reagieren auf "input", native Selects
                // und klassische Formulare zusätzlich auf "change".
                field.dispatchEvent(new Event('input', {
                    bubbles: true,
                    composed: true
                }));
                field.dispatchEvent(new Event('change', {
                    bubbles: true,
                    composed: true
                }));
            }

            return JSON.stringify({
                ok: Boolean(field),
                value: field && 'value' in field ? field.value : null
            });
        }
        if (name === 'read_html_by_query') {
            let read_val = '';
            if (args.selector && document.querySelector(args.selector)) {
               read_val = document.querySelector(args.selector).outerHTML;
            }
            return JSON.stringify({ read_val });
        }
        if (name === 'read_html_value_by_query') {
            let read_val = '';
            if (args.selector && document.querySelector(args.selector)) {
               read_val = document.querySelector(args.selector).value;
            }
            return JSON.stringify({ read_val });
        }
        if (name === 'check_if_css_selector_exists') {
            let selector_exists = false;
            if (args.selector && document.querySelector(args.selector)) {
                selector_exists = true;
            }
            return JSON.stringify({ selector_exists });
        }
        if (name === 'read_html_by_query_all') {
            let read_val = '';
            if (args.selector && document.querySelector(args.selector)) {
                document.querySelectorAll(args.selector).forEach((element) => {
                    read_val += element.outerHTML;
                });
            }
            return JSON.stringify({ read_val });
        }

        if (name === 'show_dialog') {
            let did_open = false;
            if (args.selector && document.querySelector(args.selector)) {
                document.querySelector(args.selector).showModal();
                did_open = true
            }
            return JSON.stringify({ did_open });
        }

        if (name === 'request_open_link') {
            try {
                const res = await fetch(REST + 'immowerft/v2/openai/realtime/request_open_link', {
                    method: 'POST',
                    headers: buildHeaders(),
                    body: JSON.stringify({ ...args, running_id: promptId, session_id: sessionId, security_token: sessionSecurityToken })
                });
                const data = await res.json().catch(() => ({}));
                const linkToOpen = data?.message?.link_to_open || '';
                if (linkToOpen) {
                    pendingRedirectUrl = appendQueryParam(linkToOpen, 'mw_reopen_ai', 'true');
                }
                return JSON.stringify(data || { ok: true });
            } catch (err) {
                return JSON.stringify({ ok: false, error: String(err) });
            }
        }

        const path = SERVER_TOOL_ENDPOINTS[name];
        if (path) {
            return await callServerTool(path, args);
        }
        return JSON.stringify({ ok: false, error: 'unknown_tool' });
    };

    // V2 OPTIMIERT: runCompletionLoop mit kombinierter Router+Chat-Route
    const runCompletionLoopV2Combined = async () => {
        let iterations = 0;
        let speakPromise = null;
        let finalTextRendered = false;
        let waitingForTtsRender = false;
        try {
            while (iterations < MAX_TOOL_ITERATIONS) {
                iterations++;
                const result = await postChatV2Combined();
                if (!result || !result.ok || !result.message) {
                    hideLoadingIndicator();
                    addMessageToChat('Entschuldigung, es gab ein Problem bei der Verarbeitung. Bitte versuche es erneut.', 'assistant', {
                        meta: buildMessageMeta({
                            source: 'system_error',
                            triggerSource: currentTurnSource,
                            errorCode: 'completion_failed',
                            url: sessionUrl
                        })
                    });
                    return;
                }
                const message = result.message;
                currentIntent = String(result.selected_slot || '');
                currentIntentLabel = String(result.selected_slot_label || '');
                // Assistant-Message (ggf. mit tool_calls) in den Verlauf aufnehmen
                transcript.push({
                    role: 'assistant',
                    content: message.content || null,
                    ...(message.tool_calls && message.tool_calls.length ? { tool_calls: message.tool_calls } : {})
                });

                if (message.tool_calls && message.tool_calls.length) {
                    for (const call of message.tool_calls) {
                        const fnName = call?.function?.name || '';
                        const fnArgs = call?.function?.arguments || '{}';
                        // Warte-Bubble: anzeigen, welche Funktion gerade läuft.
                        setLoadingStatusForTool(fnName, fnArgs);
                        const output = await executeToolCall(fnName, fnArgs);
                        transcript.push({ role: 'tool', tool_call_id: call.id, content: output });
                        // Funktionsaufruf für den Session-Verlauf mitschreiben.
                        if (fnName) {
                            queueBackendLog('function', describeToolCall(fnName, fnArgs, output), 'function');
                        }

                        if (fnName === 'request_open_link' && pendingRedirectUrl) {
                            const url = pendingRedirectUrl;
                            pendingRedirectUrl = null;
                            rememberRedirectContext(url);
                            flushBackendLogs();
                            window.open(url, '_self');
                            return;
                        }
                    }
                    // Tool-Ergebnisse liegen vor -> KI wertet sie aus.
                    setLoadingStatus(LOADING_STATUS_THINKING);
                    trimTranscript();
                    continue; // Antwort mit Tool-Ergebnissen erneut anfragen
                }

                // Finale Textantwort
                const finalText = (message.content || '').trim();
                if (finalText) {
                    const renderFinalText = () => {
                        if (finalTextRendered) return;
                        waitingForTtsRender = false;
                        finalTextRendered = true;
                        addMessageToChat(finalText, 'assistant', {
                            meta: buildMessageMeta({
                                intent: currentIntent,
                                intentLabel: currentIntentLabel,
                                source: currentTurnSource,
                                url: sessionUrl
                            })
                        });
                        hideLoadingIndicator();
                    };

                    if (isAudioEnabled) {
                        waitingForTtsRender = true;
                        speakPromise = speak(finalText, {
                            onStart: renderFinalText,
                            onUnavailable: renderFinalText
                        });
                    } else {
                        renderFinalText();
                    }
                } else {
                    hideLoadingIndicator();
                }
                trimTranscript();
                break;
            }
        } catch (err) {
            hideLoadingIndicator();
            addMessageToChat('Entschuldigung, es gab ein Problem bei der Verarbeitung. Bitte versuche es erneut.', 'assistant', {
                meta: buildMessageMeta({
                    intent: currentIntent,
                    intentLabel: currentIntentLabel,
                    source: 'system_error',
                    triggerSource: currentTurnSource,
                    errorCode: 'completion_exception',
                    url: sessionUrl
                })
            });
        }

        // Sicherstellen, dass die Eingabesperre in jedem Fall aufgehoben ist (idempotent).
        if (!finalTextRendered && !waitingForTtsRender) {
            hideLoadingIndicator();
        }

        // Erst JETZT (nach Antwort/TTS) die Nachrichten im Backend speichern.
        flushBackendLogs();

        if (pendingRedirectUrl) {
            const url = pendingRedirectUrl;
            pendingRedirectUrl = null;
            rememberRedirectContext(url);
            if (isAudioEnabled && speakPromise) {
                try { await speakPromise; } catch (e) {}
                window.open(url, '_self');
            } else {
                setTimeout(() => { window.open(url, '_self'); }, 300);
            }
        }
    };


    const sendUserMessage = async (text, showInChat = true) => {
        if (!text) return;
        text = String(text).trim();
        if (!text) return;
        placeholderRotator.stop();

        // Während Router/Antwort läuft (Warte-Bubble sichtbar): keine neue Eingabe annehmen
        // und NICHT queuen. Während TTS vorgelesen wird ist isTurnBusy bereits false -> erlaubt.
        if (isTurnBusy) return;

        // Ohne Consent KEINE Verbindung/REST. Ggf. die Consent-Auswahl einblenden.
        if (!hasConsent()) {
            applyConsentState();
            return;
        }

        stopSpeaking();
        updateSalutationPreferenceFromText(text);

        // Turn sofort (synchron, vor allen awaits) als "beschäftigt" markieren + Eingabe sperren.
        isTurnBusy = true;
        setInputBusy(true);
        currentTurnId = generateSessionId();
        currentTurnToken = '';

        // Session (inkl. Security-Token) sicherstellen, bevor Router/Chat-Requests rausgehen
        // (verhindert Permission-Fail beim ersten Turn).
        const readyPromise = ensureSessionStarted();
        if (readyPromise) {
            try { await readyPromise; } catch (e) {}
        }

        currentTurnSource = showInChat ? 'user' : 'external_trigger';
        currentIntent = '';
        currentIntentLabel = '';
        if (showInChat) {
            // Chat sichtbar machen, falls noch geschlossen
            if (chatbox && !chatbox.classList.contains('mwai-assistant-chatbox-active')) {
                openChat();
            }
            addMessageToChat(text, 'user', {
                meta: buildMessageMeta({ source: currentTurnSource, url: sessionUrl })
            });
        }
        // Bei showInChat=false: nur als Kontext an die KI (kein Anzeigen, kein Persistieren),
        // konsistent zum V1-Verhalten der externen Trigger.
        transcript.push({ role: 'user', content: text });
        trimTranscript();

        // Warte-Bubble anzeigen
        showLoadingIndicator();

        // V2: Router + Chat in einer Route
        await runCompletionLoopV2Combined();

        // Hier nun wieder den fokus auf die Eingabe lenken.
        inputField.focus();
    };

    // ===== Verlauf beim Laden einspielen =====
    const redirectContext = consumeRedirectContext();
    let initialHistory = loadChatHistory();
    if (redirectContext && redirectContext.history.length > 0) {
        initialHistory = mergeChatHistories(redirectContext.history, initialHistory).slice(-MAX_CHAT_HISTORY);
        saveChatHistory(initialHistory);
    }
    if (isHistoryEnabled && initialHistory.length > 0) {
        hideCtaOnFirstMessage();
        initialHistory.forEach(msg => {
            const role = msg.role === 'assistant' ? 'assistant' : 'user';
            addMessageToChat(msg.text, role, { persist: false, linkHref: msg.linkHref || '', linkLabel: msg.linkLabel || '' });
            transcript.push({ role, content: msg.text });
        });
        hasReplayedHistoryForSession = true;
    }
    if (redirectContext) {
        transcript.push({ role: 'assistant', content: redirectContext.systemMessage });
        trimTranscript();
    }

    // ===== Eingabe: Senden =====
    const submitFromInput = () => {
        // Falls das Mikro aktiv ist, Aufnahme beenden und nach Transkription absenden.
        if (isRecording || isMicStarting) {
            submitAfterRecordingStop = true;
            stopRecording();
            return;
        }
        const text = (inputField.value || '').trim();
        if (!text) return;
        inputField.value = '';
        sendUserMessage(text, true);
    };
    if (sendBtn) {
        sendBtn.addEventListener('click', function (e) {
            e.preventDefault();
            submitFromInput();
        });
    }
    inputField.addEventListener('keydown', function (e) {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            submitFromInput();
        }
    });

    // ===== CTA-Buttons (= Beispielfragen) =====
    ctaButtons.forEach(function (btn) {
        btn.addEventListener('click', function () {
            const prompt = btn.getAttribute('data-prompt');
            if (prompt) {
                if (chatbox && !chatbox.classList.contains('mwai-assistant-chatbox-active')) {
                    openChat();
                }
                sendUserMessage(prompt, true);
            }
        });
    });

    // ===== Mikrofon (Whisper) =====
    const setMicState = (state) => {
        if (!micBtn) return;
        micBtn.classList.toggle('is-recording', state === 'recording');
        micBtn.classList.toggle('is-starting', state === 'starting');
        micBtn.setAttribute('data-state', state);
    };
    const scheduleMicStreamRelease = () => {
        if (micStreamReleaseTimer) {
            clearTimeout(micStreamReleaseTimer);
            micStreamReleaseTimer = null;
        }
        if (!micStream) return;
        micStreamReleaseTimer = setTimeout(() => {
            if (!micStream || isRecording || isMicStarting) return;
            micStream.getTracks().forEach(t => t.stop());
            micStream = null;
        }, MIC_STREAM_IDLE_TIMEOUT_MS);
    };
    const ensureMicStream = async () => {
        if (micStream && micStream.active) {
            if (micStreamReleaseTimer) {
                clearTimeout(micStreamReleaseTimer);
                micStreamReleaseTimer = null;
            }
            return micStream;
        }
        try {
            micStream = await navigator.mediaDevices.getUserMedia({ audio: true });
            return micStream;
        } catch (err) {
            alert('Mikrofon-Zugriff wurde verweigert oder ist nicht verfügbar.');
            return null;
        }
    };
    const startRecording = async () => {
        if (isRecording || isMicStarting) return;
        const stream = await ensureMicStream();
        if (!stream) return;
        isMicStarting = true;
        stopRecordingAfterStart = false;
        micStartPreviousPlaceholder = inputField.getAttribute('placeholder') || inputPlaceholderDefault;
        inputField.placeholder = 'Mikrofon wird gestartet...';
        setMicState('starting');
        micStream = stream;
        mediaChunks = [];
        let mimeType = '';
        if (window.MediaRecorder) {
            if (MediaRecorder.isTypeSupported('audio/webm')) mimeType = 'audio/webm';
            else if (MediaRecorder.isTypeSupported('audio/mp4')) mimeType = 'audio/mp4';
        }
        try {
            mediaRecorder = mimeType ? new MediaRecorder(micStream, { mimeType }) : new MediaRecorder(micStream);
        } catch (e) {
            mediaRecorder = new MediaRecorder(micStream);
        }
        mediaRecorder.ondataavailable = (e) => { if (e.data && e.data.size > 0) mediaChunks.push(e.data); };
        mediaRecorder.onstart = () => {
            isMicStarting = false;
            isRecording = true;
            setMicState('recording');
            inputField.placeholder = 'Jetzt sprechen...';
            if (stopRecordingAfterStart) {
                stopRecordingAfterStart = false;
                stopRecording();
            }
        };
        mediaRecorder.onstop = onRecordingStop;
        try {
            mediaRecorder.start();
        } catch (e) {
            isMicStarting = false;
            isRecording = false;
            setMicState('idle');
            inputField.placeholder = micStartPreviousPlaceholder || inputPlaceholderDefault;
            scheduleMicStreamRelease();
        }
    };
    const stopRecording = () => {
        if (isMicStarting) {
            stopRecordingAfterStart = true;
            return;
        }
        if (mediaRecorder && mediaRecorder.state !== 'inactive') {
            mediaRecorder.stop();
        }
        isRecording = false;
        setMicState('idle');
    };
    const onRecordingStop = async () => {
        isMicStarting = false;
        isRecording = false;
        setMicState('idle');
        scheduleMicStreamRelease();
        if (!mediaChunks.length) {
            inputField.placeholder = micStartPreviousPlaceholder || inputPlaceholderDefault;
            if (submitAfterRecordingStop) {
                submitAfterRecordingStop = false;
                submitFromInput();
            }
            return;
        }
        const type = (mediaRecorder && mediaRecorder.mimeType) || 'audio/webm';
        const ext = type.includes('mp4') ? 'mp4' : 'webm';
        const blob = new Blob(mediaChunks, { type });
        mediaChunks = [];

        const prevPlaceholder = inputField.getAttribute('placeholder');
        inputField.placeholder = 'Transkribiere...';
        inputField.disabled = true;

        try {
            const form = new FormData();
            form.append('audio', blob, 'audio.' + ext);
            form.append('session_id', sessionId || '');
            form.append('security_token', sessionSecurityToken || '');
            const transcribeHeaders = {};
            if (cfg.nonce) {
                transcribeHeaders['X-WP-Nonce'] = cfg.nonce;
            }
            const res = await fetch(REST + 'immowerft/v2/openai/transcribe', {
                method: 'POST',
                headers: transcribeHeaders,
                body: form
            });
            const data = await res.json().catch(() => null);
            if (data && data.ok && data.text) {
                inputField.value = (inputField.value ? inputField.value + ' ' : '') + data.text.trim();
            }
        } catch (e) {} finally {
            inputField.disabled = false;
            inputField.placeholder = micStartPreviousPlaceholder || prevPlaceholder || inputPlaceholderDefault;
            inputField.focus();
            if (submitAfterRecordingStop) {
                submitAfterRecordingStop = false;
                submitFromInput();
            }
        }
    };
    if (micBtn) {
        micBtn.addEventListener('click', function (e) {
            e.preventDefault();
            if (!hasConsent()) { applyConsentState(); return; } // Whisper erst nach Consent
            if (isRecording || isMicStarting) {
                submitAfterRecordingStop = false;
                stopRecording();
            } else {
                startRecording();
            }
        });
    }

    // ===== Sprachausgabe-Toggle =====
    if (ttsBtn) {
        ttsBtn.addEventListener('click', function (e) {
            e.preventDefault();
            isAudioEnabled = !isAudioEnabled;
            sessionStorage.setItem('mw_ai_audio_enabled', isAudioEnabled.toString());
            updateTtsButton();
            if (!isAudioEnabled) stopSpeaking();
        });
    }

    // ===== Chat leeren =====
    const resetChat = () => {
        stopSpeaking();
        if (isHistoryEnabled) localStorage.removeItem(CHAT_HISTORY_KEY);
        if (chatWindow) chatWindow.innerHTML = '';
        transcript = [];
        chatStarted = false;
        const ctaWrap = document.querySelector('.mwai-assistant-chatbox-cta-buttons-wrapper');
        if (ctaWrap) ctaWrap.style.display = '';
    };
    if (clearBtn) {
        clearBtn.addEventListener('click', function (e) {
            e.preventDefault();
            resetChat();
        });
    }

    let sessionInitStarted = false;
    const ensureSessionStarted = () => {
        if (sessionInitStarted) return sessionReadyPromise;
        if (!hasConsent()) return null; // ohne Consent keine Session/REST
        sessionInitStarted = true;
        sessionReadyPromise = (async function initSession() {
            // Normalfall: Session-ID + Token wurden bereits vom Server injiziert -> KEIN Request.
            // Fallback: session/start aufrufen wenn nötig
            if (!sessionId || !sessionSecurityToken) {
                await startSession();
            }
            isConnectionReady = true;
            if (messageQueue.length > 0) {
                const queued = messageQueue.slice();
                messageQueue = [];
                queued.forEach(item => sendUserMessage(item.text, item.showInChat));
            }
        })();
        return sessionReadyPromise;
    };

    // ===== Consent-Warteschlange =====
    // Aktionen, die ohne Consent ausgelöst wurden (z. B. Klick oder Auto-Klick auf die
    // Bubble direkt beim Seitenaufruf), würden sonst kommentarlos verworfen. Sie werden
    // hier gepuffert und einmalig nachgeholt, sobald der Nutzer den Consent erteilt.
    let pendingConsentActions = [];

    // Merkt eine Aktion vor und gibt true zurück, wenn kein Consent vorliegt.
    // Mit einem key ersetzt ein erneuter Aufruf den bisherigen Eintrag, statt ihn zu
    // doppeln (z. B. mehrfacher Klick auf dieselbe Bubble -> nur die letzte Absicht zählt).
    const queueUntilConsent = (action, key = null) => {
        if (hasConsent()) return false;
        if (key) {
            pendingConsentActions = pendingConsentActions.filter(item => item.key !== key);
        }
        pendingConsentActions.push({ key, action });
        return true;
    };

    const flushPendingConsentActions = () => {
        if (!hasConsent() || pendingConsentActions.length === 0) return;
        const queued = pendingConsentActions.slice();
        pendingConsentActions = [];
        queued.forEach(item => {
            try { item.action(); } catch (e) {}
        });
    };

    const applyConsentState = () => {
        document.querySelector( '.mwai-asisstant-taskbar-chat-toggle' ).style.display = 'none';
        syncConsentSessionState();
        hasConsent = () => sessionStorage.getItem('mw_ai_consent') === '1';
        const consented = hasConsent();
        if (consentWith) consentWith.style.display = consented ? '' : 'none';
        if (consentWithout) consentWithout.style.display = consented ? 'none' : '';
        document.body.classList.toggle('mw_ki_chat_consent_true', consented);
        document.body.classList.toggle('mw_ki_chat_consent_false', !consented);
        syncSocketConsentClass();
        if ( true === consented ) {
            document.querySelector( '.mwai-asisstant-taskbar-chat-toggle' ).style.display = 'inline-block';
            // Active class auf die Chatbox für rechts start.
            document.querySelector('#mwai-assistant-overlay-wrapper').classList.add('active-consent')
        }
    };
    if (consentUnlockBtn) {
        consentUnlockBtn.addEventListener('click', function (e) {
            e.preventDefault();
            if ( window.CCM ) {
                window.CCM.openWidget();
            } else if ( isEnfoldConsentActive() ) {
                const enfoldConsentTrigger = document.querySelector('.avia-cookie-info-btn');
                if (enfoldConsentTrigger) {
                    enfoldConsentTrigger.click();
                }
            }
            if (inputField) inputField.focus();
        });
    }
    const showAllImmoBtn = document.querySelector('.mw-ai-v2-show-all-immo');
    if (showAllImmoBtn) {
        showAllImmoBtn.addEventListener('click', function (e) {
            e.preventDefault();
            const url = showAllImmoBtn.getAttribute('data-url');
            if (url) window.location.href = url;
        });
    }
    applyConsentState();

    // ===== Sprechblase schließen =====
    let isSpeechBubbleDismissed = false;
    try {
        isSpeechBubbleDismissed = localStorage.getItem(SPEECH_BUBBLE_DISMISSED_STORAGE_KEY) === '1';
    } catch (e) {}

    if (isSpeechBubbleDismissed && speechBubble) {
        speechBubble.style.display = 'none';
    }

    if (speechBubbleClose && speechBubble) {
        speechBubbleClose.addEventListener('click', function () {
            speechBubble.style.display = 'none';
            try {
                localStorage.setItem(SPEECH_BUBBLE_DISMISSED_STORAGE_KEY, '1');
            } catch (e) {}
        });
    }
    let current_magic_btn = document.querySelector( '.mwai-assistant-taskbar-magic-button-text' );
    if ( current_magic_btn ) {
        current_magic_btn.addEventListener('click', function () {
            const bubbleAction = current_magic_btn.getAttribute('data-bubble-btn-action');
            if ( ! bubbleAction ) {
                // Ohne Pipe in `intro` gibt es keine konkrete Aktion. Die Bubble
                // dient dann als Einstieg in einen frischen Chat mit den CTA-Buttons.
                resetChat();
                openChat();
                if (speechBubbleClose) speechBubbleClose.click();
                return;
            }
            if ( current_magic_btn.getAttribute('data-bubble-btn-clicked') === '1' ) return;
            current_magic_btn.setAttribute('data-bubble-btn-clicked', '1');

            const runBubbleAction = () => {
                sendUserMessage(bubbleAction, true).then(r => {});
                if (speechBubbleClose) speechBubbleClose.click();
            };

            // Ohne Consent verwirft sendUserMessage die Nachricht kommentarlos. Stattdessen
            // still vormerken: kein Chat-Auto-Open, die Bubble bleibt sichtbar. Sobald der
            // Nutzer den Consent erteilt, läuft runBubbleAction automatisch nach.
            if ( queueUntilConsent(runBubbleAction, 'bubble') ) {
                applyConsentState();
                return;
            }

            runBubbleAction();
        });
    }

    function openInfoTextModal(title, text) {
        const existingModal = document.querySelector('.mw-ai-v2-info-text-modal');
        if (existingModal) {
            existingModal.remove();
        }

        const modal = document.createElement('div');
        modal.className = 'mw-ai-v2-info-text-modal';
        modal.setAttribute('role', 'dialog');
        modal.setAttribute('aria-modal', 'true');

        const panel = document.createElement('div');
        panel.className = 'mw-ai-v2-info-text-modal__panel';

        const closeButton = document.createElement('button');
        closeButton.type = 'button';
        closeButton.className = 'mw-ai-v2-info-text-modal__close';
        closeButton.setAttribute('aria-label', cfg.close_aria_label || 'Schließen');
        closeButton.innerHTML = '<svg viewBox="0 0 24 24" width="16" height="16" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" fill="none"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>';

        const heading = document.createElement('div');
        heading.className = 'mw-ai-v2-info-text-modal__title';
        heading.textContent = title || 'Info';

        const body = document.createElement('div');
        body.className = 'mw-ai-v2-info-text-modal__body';
        body.innerHTML = text;

        panel.appendChild(closeButton);
        panel.appendChild(heading);
        panel.appendChild(body);
        modal.appendChild(panel);
        document.body.appendChild(modal);

        const closeModal = () => {
            modal.remove();
            document.removeEventListener('keydown', onKeydown);
        };
        const onKeydown = (event) => {
            if (event.key === 'Escape') {
                closeModal();
            }
        };

        closeButton.addEventListener('click', closeModal);
        modal.addEventListener('click', (event) => {
            if (event.target === modal) {
                closeModal();
            }
        });
        document.addEventListener('keydown', onKeydown);
        closeButton.focus();
    }

    function isInfoButtonModalActive(button) {
        if (button.getAttribute('data-modal-schedule-enabled') !== '1') {
            return button.getAttribute('data-open-modal') === '1';
        }

        let schedule = {};
        try {
            schedule = JSON.parse(button.getAttribute('data-modal-schedule') || '{}');
        } catch (error) {
            schedule = {};
        }

        const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
        const todaySchedule = schedule[dayNames[new Date().getDay()]];
        const intervals = Array.isArray(todaySchedule)
            ? todaySchedule
            : (todaySchedule && typeof todaySchedule === 'object' ? [todaySchedule] : []);
        const now = new Date();
        const currentMinutes = now.getHours() * 60 + now.getMinutes();
        return intervals.some(interval => {
            if (!interval || !/^([01]\d|2[0-3]):[0-5]\d$/.test(interval.start || '') || !/^([01]\d|2[0-3]):[0-5]\d$/.test(interval.end || '')) {
                return false;
            }
            const [startHour, startMinute] = interval.start.split(':').map(Number);
            const [endHour, endMinute] = interval.end.split(':').map(Number);
            const startMinutes = startHour * 60 + startMinute;
            const endMinutes = endHour * 60 + endMinute;
            return endMinutes > startMinutes && currentMinutes >= startMinutes && currentMinutes < endMinutes;
        });
    }

    document.addEventListener('click', function (event) {
        const infoPromptBtn = event.target.closest('.mw-ai-v2-info-prompt-button');
        if (!infoPromptBtn) return;

        event.preventDefault();
        const promptText = (infoPromptBtn.getAttribute('data-prompt') || '').trim();
        const modalText = (infoPromptBtn.getAttribute('data-modal-content') || '').trim() || promptText;
        const showModal = isInfoButtonModalActive(infoPromptBtn);
        if (showModal) {
            if (!modalText) return;
            openInfoTextModal(infoPromptBtn.getAttribute('data-modal-title') || infoPromptBtn.textContent.trim(), modalText);
            return;
        }
        if (!promptText) return;

        const runInfoPromptAction = () => {
            sendUserMessage(promptText, true).then(r => {});
        };

        if (queueUntilConsent(runInfoPromptAction, 'info_prompt:' + promptText)) {
            applyConsentState();
            return;
        }

        runInfoPromptAction();
    });

    // ===== Externe Trigger + Queue =====
    document.addEventListener('mw_ai_send_message', function (event) {
        if (event.detail && event.detail.text) {
            if (!isConnectionReady) {
                messageQueue.push({ text: event.detail.text, showInChat: false });
            } else {
                sendUserMessage(event.detail.text, false);
            }
        }
    });
    document.addEventListener('mw_ai_send_message_show', function (event) {
        if (event.detail && event.detail.text) {
            if (!isConnectionReady) {
                messageQueue.push({ text: event.detail.text, showInChat: true });
            } else {
                sendUserMessage(event.detail.text, true);
            }
        }
    });

    // ===== Chatbox öffnen/schließen, Drag, Fullscreen, Infomodal (MWAI-UI) =====
    function openChat({ focusInput = false } = {}) {
        if (!chatbox || !toggleTab) return;
        chatbox.classList.add('mwai-assistant-chatbox-active');
        toggleTab.classList.add('mwai-assistant-chatbox-toggle-active');
        overlay.classList.add('chat-open');
        lockMobilePageScroll();
        document.querySelector( '.mwai-assistant-taskbar-magic-buttons-wrapper' ).style.display = 'none';
        if (toggleText) toggleText.textContent = toggleTab.getAttribute('data-close-label') || 'Chat schließen';
        try { localStorage.setItem('mw_ai_v2_chat_open', '1'); } catch (e) {}

        // Nur beim manuellen Öffnen über den Toggle fokussieren.
        // Magic-/Prompt-Buttons dürfen den Fokus nicht automatisch übernehmen.
        if (focusInput) {
            inputField.focus();
        }
    }
    function closeChat() {
        if (!chatbox || !toggleTab) return;
        chatbox.classList.remove('mwai-assistant-chatbox-active');
        toggleTab.classList.remove('mwai-assistant-chatbox-toggle-active');
        overlay.classList.remove('chat-open');
        unlockMobilePageScroll();
        if (toggleText) toggleText.textContent = toggleTab.getAttribute('data-open-label') || 'Chat öffnen';
        try { localStorage.setItem('mw_ai_v2_chat_open', '0'); } catch (e) {}
        stopSpeaking();
        setTimeout(() => {
            chatbox.classList.remove('mwai-assistant-chatbox-detached');
            chatbox.classList.remove('mwai-assistant-chatbox-fullscreen');
            chatbox.style.left = '';
            chatbox.style.top = '';
            chatbox.style.width = '';
            chatbox.style.height = '';
        }, 300);
    }
    if (toggleTab) {
        toggleTab.addEventListener('click', function () {
            if (chatbox.classList.contains('mwai-assistant-chatbox-active')) {
                closeChat();
            } else {
                openChat({ focusInput: true });
            }
        });
    }
    if (chatbox) {
        const headerCloseBtn = chatbox.querySelector('.mwai-assistant-chatbox-header-button-close');
        if (headerCloseBtn) headerCloseBtn.addEventListener('click', closeChat);
    }

    // Drag & Drop
    (function initDragAndDrop() {
        if (!chatbox) return;
        const header = chatbox.querySelector('.mwai-assistant-chatbox-header-wrapper');
        if (!header) return;

        const STORAGE_KEY = 'mwai_chatbox_position';
        let isDragging = false, startX, startY, initialLeft, initialTop;

        // --- POSITION RESTAURIEREN ---
        try {
            const savedPos = localStorage.getItem(STORAGE_KEY);
            if (savedPos) {
                const pos = JSON.parse(savedPos);
                if (typeof pos.left === 'number' && typeof pos.top === 'number') {
                    chatbox.classList.add('mwai-assistant-chatbox-detached');
                    chatbox.style.left = pos.left + 'px';
                    chatbox.style.top = pos.top + 'px';
                }
            }
        } catch (err) {
            // Ignorieren, falls LocalStorage blockiert ist
        }

        header.addEventListener('mousedown', (e) => {
            const isDragBtn = e.target.closest('.mwai-assistant-chatbox-header-button-drag');
            const isButton = e.target.closest('button');
            const isDetached = chatbox.classList.contains('mwai-assistant-chatbox-detached');
            const isFullscreen = chatbox.classList.contains('mwai-assistant-chatbox-fullscreen');
            if (!isDragBtn) {
                if (isFullscreen) return;
                if (!isDetached || isButton) return;
            }
            e.preventDefault();
            isDragging = true;
            const rect = chatbox.getBoundingClientRect();
            if (isFullscreen) chatbox.classList.remove('mwai-assistant-chatbox-fullscreen');
            if (!isDetached || isFullscreen) {
                chatbox.classList.add('mwai-assistant-chatbox-detached');
                let newLeft = rect.left;
                let newTop = rect.top;
                if (e.clientX > newLeft + 340) newLeft = e.clientX - 300;
                chatbox.style.left = newLeft + 'px';
                chatbox.style.top = newTop + 'px';
            }
            chatbox.classList.add('mwai-assistant-chatbox-dragging');
            const newRect = chatbox.getBoundingClientRect();
            startX = e.clientX; startY = e.clientY;
            initialLeft = newRect.left; initialTop = newRect.top;
            document.addEventListener('mousemove', onMouseMove);
            document.addEventListener('mouseup', onMouseUp);
        });

        function onMouseMove(e) {
            if (!isDragging) return;
            let newLeft = initialLeft + (e.clientX - startX);
            let newTop = initialTop + (e.clientY - startY);
            const maxLeft = document.documentElement.clientWidth - chatbox.offsetWidth;
            const maxTop = window.innerHeight - chatbox.offsetHeight - 65;
            let clampedLeft = Math.max(0, Math.min(newLeft, maxLeft));
            let clampedTop = Math.max(0, Math.min(newTop, maxTop));
            if (newLeft !== clampedLeft) { startX = e.clientX; initialLeft = clampedLeft; }
            if (newTop !== clampedTop) { startY = e.clientY; initialTop = clampedTop; }
            chatbox.style.left = clampedLeft + 'px';
            chatbox.style.top = clampedTop + 'px';
        }

        function onMouseUp() {
            if (!isDragging) return;
            isDragging = false;
            chatbox.classList.remove('mwai-assistant-chatbox-dragging');
            document.removeEventListener('mousemove', onMouseMove);
            document.removeEventListener('mouseup', onMouseUp);

            // --- POSITION SPEICHERN ---
            if (chatbox.classList.contains('mwai-assistant-chatbox-detached') &&
                !chatbox.classList.contains('mwai-assistant-chatbox-fullscreen')) {
                const rect = chatbox.getBoundingClientRect();
                try {
                    localStorage.setItem(STORAGE_KEY, JSON.stringify({
                        left: rect.left,
                        top: rect.top
                    }));
                } catch (err) {}
            }
        }
    })();

    // Fullscreen
    (function initFullscreen() {
        if (!chatbox) return;
        const fullscreenBtn = chatbox.querySelector('.mwai-assistant-chatbox-header-button-fullscreen');
        if (!fullscreenBtn) return;

        // Derselbe Key, damit wir beim Reset die gezogene Position im Speicher löschen können
        const STORAGE_KEY = 'mwai_chatbox_position';

        fullscreenBtn.addEventListener('click', () => {
            const isFullscreen = chatbox.classList.contains('mwai-assistant-chatbox-fullscreen');

            if (!isFullscreen) {
                // 1. KLICK: Vollbild AKTIVIEREN
                chatbox.classList.add('mwai-assistant-chatbox-fullscreen');

                // Inline-Styles für Vollbild zurücksetzen
                chatbox.style.left = '';
                chatbox.style.top = '';
                chatbox.style.width = '';
                chatbox.style.height = '';
            } else {
                // 2. KLICK: ZURÜCK IN DIE INITIALE AUSGANGSLAGE (CSS-Standard)
                chatbox.classList.remove('mwai-assistant-chatbox-fullscreen');

                // WICHTIG: Detach-Klasse entfernen, damit das Standard-Layout (z.B. unten rechts) greift
                chatbox.classList.remove('mwai-assistant-chatbox-detached');

                // Alle Inline-Positions-Styles säubern
                chatbox.style.left = '';
                chatbox.style.top = '';
                chatbox.style.width = '';
                chatbox.style.height = '';

                // Gespeicherte Drag-Position aus dem Storage löschen!
                // So startet die Box auch nach einem Reload wieder in der primären Ausgangslage.
                try {
                    localStorage.removeItem(STORAGE_KEY);
                } catch (err) {}
            }
        });
    })();

    // Infomodal
    (function initInfoModal() {
        const modalWrapper = document.querySelector('.mw-assistant-taskbar-content-infomodal-content-wrapper');
        const toggleBtn = document.getElementById('mw-aissistant-taskbar-infomodal-buttonn');
        const closeBtn = document.querySelector('.mw-assistant-taskbar-content-infomodal-close-button');
        if (!modalWrapper || !toggleBtn || !closeBtn) return;
        const originalText = toggleBtn.textContent;
        function toggleModal() {
            const isActive = modalWrapper.classList.contains('mw-assistant-taskbar-content-infomodal-active');
            if (isActive) {
                modalWrapper.classList.remove('mw-assistant-taskbar-content-infomodal-active');
                toggleBtn.classList.remove('active');
                toggleBtn.textContent = originalText;
                toggleBtn.style.minWidth = '';
            } else {
                toggleBtn.style.minWidth = toggleBtn.offsetWidth + 'px';
                modalWrapper.classList.add('mw-assistant-taskbar-content-infomodal-active');
                toggleBtn.classList.add('active');
                toggleBtn.textContent = 'Schließen';
            }
        }
        toggleBtn.addEventListener('click', toggleModal);
        closeBtn.addEventListener('click', toggleModal);
    })();

    // ===== Initialisierung =====
    // Session nur starten, wenn Consent bereits vorliegt (sonst erst beim Freischalten).
    if (hasConsent()) {
        ensureSessionStarted();
    }

    // Chatbox-Zustand wiederherstellen: nur öffnen, wenn zuvor offen war (localStorage) oder
    // per URL-Parameter angefordert. Default: geschlossen.
    const searchParams = new URLSearchParams(window.location.search);
    let wasChatOpen = false;
    try { wasChatOpen = localStorage.getItem('mw_ai_v2_chat_open') === '1'; } catch (e) {}
    if ( ( searchParams.get('mw_reopen_ai') === 'true' || wasChatOpen && hasConsent() ) && false === isMobileDevice ) {
        openChat();
    }

    // Session beim Verlassen beenden
    window.addEventListener('beforeunload', function () {
        endSession();
    });

    // ===== URL-Parameter Bubble-Trigger =====
    // ?mw_ai_assistant_text=... öffnet den Chat und zeigt direkt eine Assistant-Nachricht.
    // ?mw_ai_visible_text=...&mw_ai_action_text=...&mw_ai_delay=3&mw_ai_auto_click=1&mw_ai_click_delay=2&mw_ai_auto_hide=10&mw_ai_auto_click_when_chat_open=1&mw_ai_assistant_text_when_chat_open=...
    (function initUrlBubbleTrigger() {
        const urlParams = new URLSearchParams(window.location.search);
        const directAssistantText = (urlParams.get('mw_ai_assistant_text') || '').trim();

        if (directAssistantText) {
            const assistantMessageKey = 'mw_ai_assistant_text_shown:' + directAssistantText;
            const showAssistantMessage = () => {
                try {
                    if (sessionStorage.getItem(assistantMessageKey) === '1') return;
                    sessionStorage.setItem(assistantMessageKey, '1');
                } catch (e) {}

                openChat();
                    addMessageToChat(directAssistantText, 'assistant', {
                        meta: buildMessageMeta({ source: 'proactive', url: sessionUrl })
                    });
                transcript.push({ role: 'assistant', content: directAssistantText });
                trimTranscript();
                flushBackendLogs();
            };

            if (queueUntilConsent(showAssistantMessage, 'url_assistant_text')) {
                applyConsentState();
                return;
            }

            showAssistantMessage();
            return;
        }

        const visibleText = urlParams.get('mw_ai_visible_text');
        const actionText = urlParams.get('mw_ai_action_text');
        if (!visibleText || !actionText) return;

        const showDelay = parseInt(urlParams.get('mw_ai_delay')) || 0;   // Sekunden bis die Bubble erscheint
        const clickDelay = parseInt(urlParams.get('mw_ai_click_delay')) || 0; // Sekunden NACH dem Erscheinen bis zum Klick
        const autoHide = parseInt(urlParams.get('mw_ai_auto_hide')) || 0;
        const autoClickRaw = urlParams.get('mw_ai_auto_click');
        const autoClick = autoClickRaw === '1' || autoClickRaw === 'true';
        const autoClickWhenChatOpenRaw = urlParams.get('mw_ai_auto_click_when_chat_open');
        const autoClickWhenChatOpen = autoClickWhenChatOpenRaw === '1' || autoClickWhenChatOpenRaw === 'true';
        const assistantTextWhenChatOpen = urlParams.get('mw_ai_assistant_text_when_chat_open') || '';

        window.mw_ai_bubble_setup_chat_bubble_trigger({
            visibleText: visibleText,
            aiActionText: actionText,
            idleSeconds: showDelay,
            autoHideSeconds: autoHide || undefined,
            autoClickWhenChatOpen: autoClickWhenChatOpen,
            assistantTextWhenChatOpen: assistantTextWhenChatOpen
        });

        // Bubble automatisch klicken -> löst aiActionText als Nachricht aus.
        if (autoClick) {
            setTimeout(() => {
                const bubbleBtn = document.querySelector('.mwai-assistant-taskbar-magic-button-text');
                if (bubbleBtn) bubbleBtn.click();
            }, (showDelay + clickDelay) * 1000);
        }
    })();

    window.dispatchEvent(new CustomEvent('mw-ai-bubble-trigger-ready', {
        detail: {
            setup: window.mw_ai_bubble_setup_chat_bubble_trigger
        }
    }));

});
